import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '@store/useAuthStore';
import useMetadataStore from '@store/useMetadataStore';
import useGameStore from '@store/useGameStore';
import useSelectionStore from '@store/useSelectionStore';
import useConfigStore from '@store/useConfigStore';
import { loginApi, logoutApi, registerApi } from '@/api/userApi';
import { saveConfigApi } from '@/api/configApi';
import feedback from '@/components/ui/general/feedback/feedback';

export default function useAuth() {
  const navigate = useNavigate();

  // 1. Get the actions directly from the store.
  const { setAuthState, clearAuthState } = useAuthStore.getState();
  
  // 2. Subscribe to the user data for UI components.
  const user = useAuthStore((state) => state.user);

  // 3. Subscribe to metadata loading state
  const isLoadingMetadata = useMetadataStore((state) => state.loading);

  // 3. The login mutation now simply calls `setAuthState` on success.
  const loginMutation = useMutation({
    mutationFn: (credentials) => loginApi(credentials.email, credentials.password),
    onSuccess: async (response) => {
      // The `persist` middleware will automatically save this to localStorage.
      setAuthState(response.data);

      // Sync local config settings to backend if they differ (e.g., volume changed in login page)
      const backendConfig = response.data.user?.properties?.config;
      await useConfigStore.getState().syncToBackendIfNeeded(backendConfig, saveConfigApi);

      // Load metadata (required for dashboard)
      await useMetadataStore.getState().fetchMetadata(true);

      // Navigate based on whether user has players
      const hasPlayers = response.data.players?.length > 0;
      navigate(hasPlayers ? '/dashboard' : '/onboarding');
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logoutApi,
    onSuccess: () => {
      // Clear all stores to prevent stale state between users
      useGameStore.getState().clearGame();
      useSelectionStore.getState().clearAllSelections();
      // The `persist` middleware will automatically clear localStorage.
      clearAuthState();
      feedback.success('👋 ¡Hasta luego!');
    },
  });

  const registerMutation = useMutation({
    mutationFn: (data) => registerApi(
      data.name,
      data.email,
      data.password,
      data.password_confirmation,
      data.terms_accepted
    ),
    onSuccess: async (response) => {
      // If email verification is required, don't set auth state yet
      if (response.data.requires_email_verification) {
        return;
      }
      // Otherwise, set auth state (token + user)
      setAuthState(response.data);

      // Sync local config settings to backend (new user gets local preferences)
      const backendConfig = response.data.user?.properties?.config;
      await useConfigStore.getState().syncToBackendIfNeeded(backendConfig, saveConfigApi);

      // Load metadata (required for onboarding/dashboard)
      await useMetadataStore.getState().fetchMetadata(true);

      // Navigate to onboarding (new user needs to create player)
      navigate('/onboarding');
    },
  });

  return {
    user,
    isLoadingUser: loginMutation.isPending || logoutMutation.isPending,
    isLoadingMetadata,
    login: (credentials, options = {}) => {
      loginMutation.mutate(credentials, {
        onSuccess: () => {
          // Navigation happens in loginMutation.onSuccess after metadata loads
          options.onSuccess?.();
        },
      });
    },
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    logout: () => {
      logoutMutation.mutate(undefined, {
        onSuccess: () => navigate('/'),
      });
    },
    isLoggingOut: logoutMutation.isPending,
    register: (data, options = {}) => {
      registerMutation.mutate(data, {
        onSuccess: (response) => {
          if (response.data.requires_email_verification) {
            // Navigate to verify email page
            navigate('/verify-email');
          }
          // Navigation to /onboarding happens in registerMutation.onSuccess after metadata loads
          options.onSuccess?.(response);
        },
        onError: options.onError,
      });
    },
    isRegistering: registerMutation.isPending,
    registerError: registerMutation.error,
  };
}